import { getStorageSession, registerStorageResponse } from './storageUrls.js';
/**
 * VisionGenerations adapter — requesting an AI image generation for a project image and
 * listing an image's generations on top of the `protos.vision.generations.v1.VisionGenerations`
 * gRPC-web service.
 *
 * `RequestGeneration` queues the work and returns immediately with the pending generation id;
 * the finished (or failed) generation arrives over realtime (`generation-complete` /
 * `generation-failed`). `ListGenerationsByImage` returns each generation with a short-TTL
 * presigned media URL for the page's initial load + refresh.
 */

import { createClient, Code, ConnectError } from '@connectrpc/connect';
import { createGrpcWebTransport } from '@connectrpc/connect-web';
import { ErrorInfoSchema } from '@buf/googleapis_googleapis.bufbuild_es/google/rpc/error_details_pb';
import { VisionGenerations } from '../gen/vision/generations/v1/generations_service_pb';
import {
  creditSourceToProto,
  cropToProto,
  generationToLegacy,
  likesResponseToLegacy,
  listBoardResponseToGenerations,
  listResponseToGenerations,
  recentPromptsResponseToLegacy,
  statusToLegacy,
} from './visionGenerationsMapping';
import { toProtoGradingSettings } from './gradingSettingsMapping.js';
import { regionsToProto } from './visionMaskRegionsMapping.js';
import { config } from '../config';
import { getIdTokenForRequest } from './firebaseClient.js';

const STORAGE_LIMIT_EXCEEDED = 'STORAGE_LIMIT_EXCEEDED';
const INSUFFICIENT_CREDITS = 'INSUFFICIENT_CREDITS';

const BASE_URL = config.VISION_GRPC_BASE_URL;
const MEDIA_BASE_URL = config.VISION_MEDIA_BASE_URL;

const PAGE_SIZE = 100;

const authInterceptor = (next) => async (req) => {
  const token = await getIdTokenForRequest();
  if (token) {
    req.header.set('authorization', `Bearer ${token}`);
  }
  const session = getStorageSession();
  const response = await next(req);
  registerStorageResponse(response.message, session);
  return response;
};

let cachedClient = null;

function client() {
  if (!cachedClient) {
    const transport = createGrpcWebTransport({
      baseUrl: BASE_URL,
      interceptors: [authInterceptor],
    });
    cachedClient = createClient(VisionGenerations, transport);
  }
  return cachedClient;
}

/**
 * Map a gRPC request error to the legacy error shape the generation page handles. Both quota
 * conditions are `ResourceExhausted`; the `ErrorInfo` reason tells them apart so the matching
 * `STORAGE_LIMIT_EXCEEDED` / `INSUFFICIENT_CREDITS` toast fires. A reasonless `ResourceExhausted`
 * falls back to insufficient credits (the legacy default); any other error passes through.
 */
export function toLegacyRequestError(error) {
  if (!(error instanceof ConnectError) || error.code !== Code.ResourceExhausted) {
    return error;
  }

  if (error.findDetails(ErrorInfoSchema)[0]?.reason === STORAGE_LIMIT_EXCEEDED) {
    const storage = new Error('Storage limit exceeded');
    storage.code = STORAGE_LIMIT_EXCEEDED;
    return storage;
  }

  const insufficient = new Error('Insufficient credits');
  insufficient.code = INSUFFICIENT_CREDITS;
  return insufficient;
}

/** Build the `RequestGeneration` workflow oneof: a `parentGenerationId` selects the refine arm, otherwise the standard-image arm. */
export function buildRequestWorkflow({ imageId, parentGenerationId, prompt, aspectRatio, keepFraming, useThinking, colorReferencePresetId, additionalReferenceAssetIds }) {
  const shared = {
    prompt: prompt || '',
    aspectRatio: aspectRatio || '',
    keepFraming: Boolean(keepFraming),
    useThinking: Boolean(useThinking),
    colorReferencePresetId: colorReferencePresetId || '',
    additionalReferenceAssetIds: additionalReferenceAssetIds || [],
  };

  if (parentGenerationId) {
    return { case: 'refine', value: { parentGenerationId, ...shared } };
  }

  return { case: 'standardImage', value: { imageId, ...shared } };
}

/**
 * Queue an image generation. Resolves to `{ generationId, status }` for the optimistic pending
 * row; the finished generation arrives over realtime.
 */
export async function requestGeneration({ projectId, creditSource, imageId, parentGenerationId, prompt, aspectRatio, keepFraming, useThinking, colorReferencePresetId, additionalReferenceAssetIds }) {
  let response;
  try {
    response = await client().requestGeneration({
      params: {
        projectId,
        creditSource: creditSourceToProto(creditSource),
        workflow: buildRequestWorkflow({ imageId, parentGenerationId, prompt, aspectRatio, keepFraming, useThinking, colorReferencePresetId, additionalReferenceAssetIds }),
      },
    });
  } catch (error) {
    throw toLegacyRequestError(error);
  }

  const result = response.result;
  if (!result?.generationId) {
    throw new Error('RequestGeneration returned no generation id');
  }

  return { generationId: result.generationId, status: statusToLegacy(result.status) };
}

/** The retried generation keeps its id; its terminal outcome arrives over realtime, not here. */
export async function regenerateGeneration({ generationId, creditSource }) {
  let response;
  try {
    response = await client().regenerateGeneration({
      params: {
        generationId,
        creditSource: creditSourceToProto(creditSource),
      },
    });
  } catch (error) {
    throw toLegacyRequestError(error);
  }

  const result = response.result;
  if (!result?.generationId) {
    throw new Error('RegenerateGeneration returned no generation id');
  }

  return { generationId: result.generationId, status: statusToLegacy(result.status) };
}

/** Separate from `requestGeneration`: that path's standard/refine discriminator is "has a parent", and an upscale also has a parent. */
// Without a parent generation the image original itself is upscaled.
export async function requestUpscale({ projectId, creditSource, imageId, parentGenerationId }) {
  let response;
  try {
    response = await client().requestGeneration({
      params: {
        projectId,
        creditSource: creditSourceToProto(creditSource),
        workflow: { case: 'upscale', value: { imageId, parentGenerationId: parentGenerationId ?? '' } },
      },
    });
  } catch (error) {
    throw toLegacyRequestError(error);
  }

  const result = response.result;
  if (!result?.generationId) {
    throw new Error('RequestGeneration returned no generation id');
  }

  return { generationId: result.generationId, status: statusToLegacy(result.status) };
}

/** Like `requestUpscale`, a dedicated arm — recreate carries only the recreated generation id; the server walks its refinement chain and derives everything else. */
export async function requestRecreate({ projectId, creditSource, parentGenerationId }) {
  let response;
  try {
    response = await client().requestGeneration({
      params: {
        projectId,
        creditSource: creditSourceToProto(creditSource),
        workflow: { case: 'recreate', value: { parentGenerationId } },
      },
    });
  } catch (error) {
    throw toLegacyRequestError(error);
  }

  const result = response.result;
  if (!result?.generationId) {
    throw new Error('RequestGeneration returned no generation id');
  }

  return { generationId: result.generationId, status: statusToLegacy(result.status) };
}

/** A dedicated arm — import-scene-look carries the target image, the source image whose look to lift, and the clear-scene flag; the server resolves the source's display generation + chain. */
export async function requestImportSceneLook({ projectId, creditSource, imageId, sourceImageId, clearScene }) {
  let response;
  try {
    response = await client().requestGeneration({
      params: {
        projectId,
        creditSource: creditSourceToProto(creditSource),
        workflow: { case: 'importSceneLook', value: { imageId, sourceImageId, clearScene } },
      },
    });
  } catch (error) {
    throw toLegacyRequestError(error);
  }

  const result = response.result;
  if (!result?.generationId) {
    throw new Error('RequestGeneration returned no generation id');
  }

  return { generationId: result.generationId, status: statusToLegacy(result.status) };
}

/** A dedicated arm — set dressing dresses an image with one set-dressing reference; the server runs
 * the two-step extract-then-generate pipeline and derives the prompt. */
export async function requestSetDressing({ projectId, creditSource, imageId, setDressingReferenceAssetId, aspectRatio, clearScene, sourceGenerationId }) {
  let response;
  try {
    response = await client().requestGeneration({
      params: {
        projectId,
        creditSource: creditSourceToProto(creditSource),
        workflow: {
          case: 'setDressing',
          value: {
            imageId,
            setDressingReferenceAssetId,
            aspectRatio,
            clearScene,
            sourceGenerationId: sourceGenerationId ?? '',
          },
        },
      },
    });
  } catch (error) {
    throw toLegacyRequestError(error);
  }

  const result = response.result;
  if (!result?.generationId) {
    throw new Error('RequestGeneration returned no generation id');
  }

  return { generationId: result.generationId, status: statusToLegacy(result.status) };
}

/** A dedicated arm — mask inpaint edits the painted regions of a source (the image original, or a
 * parent generation when `parentGenerationId` is set); the server rasterizes the labeled mask and
 * runs the two-step layout-prompt-then-render pipeline. */
export async function requestInpaint({ projectId, creditSource, imageId, parentGenerationId, aspectRatio, regions }) {
  let response;
  try {
    response = await client().requestGeneration({
      params: {
        projectId,
        creditSource: creditSourceToProto(creditSource),
        workflow: {
          case: 'maskInpaint',
          value: {
            imageId,
            parentGenerationId: parentGenerationId ?? '',
            aspectRatio: aspectRatio ?? '',
            regions: regionsToProto(regions),
          },
        },
      },
    });
  } catch (error) {
    throw toLegacyRequestError(error);
  }

  const result = response.result;
  if (!result?.generationId) {
    throw new Error('RequestGeneration returned no generation id');
  }

  return { generationId: result.generationId, status: statusToLegacy(result.status) };
}

/** Persist a generation's live mask regions (replace-all), returning the updated generation
 * record. */
export async function setGenerationMaskRegions({ generationId, regions }) {
  const response = await client().setGenerationMaskRegions({
    params: { generationId, regions: regionsToProto(regions) },
  });

  if (!response.result?.generation) {
    throw new Error('SetGenerationMaskRegions returned no generation');
  }

  return generationToLegacy(response.result.generation);
}

/** List an image's generations (paging through to load them all), as legacy generation records. */
export async function listGenerationsByImage(projectId, imageId, { shouldContinue } = {}) {
  const generations = [];
  let pageToken = null;

  do {
    if (shouldContinue && !shouldContinue()) throw new DOMException('Image renewal paused', 'AbortError');
    const pagination = { pageSize: PAGE_SIZE };
    if (pageToken) {
      pagination.pageToken = { case: 'nextToken', value: pageToken };
    }

    const response = await client().listGenerationsByImage({
      params: { projectId, imageId },
      pagination,
    });

    const { generations: pageGenerations, nextPageToken } = listResponseToGenerations(response);
    generations.push(...pageGenerations);
    pageToken = nextPageToken;
  } while (pageToken);

  return generations;
}

export async function listRecentPrompts(projectId) {
  const response = await client().listRecentPrompts({ params: { projectId } });

  return recentPromptsResponseToLegacy(response);
}

export async function likeGeneration(generationId) {
  const response = await client().likeGeneration({
    params: { generationId },
  });

  return likesResponseToLegacy(response);
}

export async function unlikeGeneration(generationId) {
  const response = await client().unlikeGeneration({
    params: { generationId },
  });

  return likesResponseToLegacy(response);
}

export async function setSelectedForDisplay(generationId) {
  await client().setSelectedForDisplay({ params: { generationId } });
}

export async function clearSelectedForDisplay(generationId) {
  await client().clearSelectedForDisplay({ params: { generationId } });
}

export async function deleteGeneration(generationId) {
  await client().deleteGeneration({ params: { generationId } });
}

export async function setGenerationCrop(generationId, cropParams) {
  const response = await client().setGenerationCrop({
    params: { generationId, crop: cropToProto(cropParams) },
  });

  if (!response.result?.generation) {
    throw new Error('SetGenerationCrop returned no generation');
  }

  return generationToLegacy(response.result.generation);
}

export async function clearGenerationCrop(generationId) {
  const response = await client().clearGenerationCrop({
    params: { generationId },
  });

  if (!response.result?.generation) {
    throw new Error('ClearGenerationCrop returned no generation');
  }

  return generationToLegacy(response.result.generation);
}

async function uploadGradedImage(projectId, generationId, blob) {
  const token = await getIdTokenForRequest();
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('image', blob, 'graded.jpg');

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${MEDIA_BASE_URL}/vision/projects/${projectId}/generations/${generationId}/grading`);

    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const { graded_upload_id: gradedUploadId } = JSON.parse(xhr.responseText);
          if (gradedUploadId) {
            resolve(gradedUploadId);
          } else {
            reject(new Error('The graded image upload returned no id.'));
          }
        } catch {
          reject(new Error('The graded image upload returned an invalid response.'));
        }
      } else if (xhr.status === 403 || xhr.status === 404) {
        reject(new Error('You do not have access to grade this generation.'));
      } else {
        reject(new Error('Failed to upload the graded image.'));
      }
    };

    xhr.onerror = () => reject(new Error('Network error. Please check your connection.'));

    xhr.send(formData);
  });
}

export async function applyGrading({ projectId, generationId, settings, blob }) {
  const gradedUploadId = await uploadGradedImage(projectId, generationId, blob);

  const response = await client().applyGenerationGrading({
    params: { generationId, gradingSettings: toProtoGradingSettings(settings), gradedUploadId },
  });

  if (!response.result?.generation) {
    throw new Error('ApplyGenerationGrading returned no generation');
  }

  return generationToLegacy(response.result.generation);
}

export async function clearGrading(generationId) {
  const response = await client().clearGenerationGrading({
    params: { generationId },
  });

  if (!response.result?.generation) {
    throw new Error('ClearGenerationGrading returned no generation');
  }

  return generationToLegacy(response.result.generation);
}

/** Queue a Vision Board text-to-image generation. Resolves to `{ generationId, status }`. */
export async function requestVisionBoardGeneration({ projectId, prompt, aspectRatio, creditSource, useThinking, additionalReferenceAssetIds }) {
  let response;
  try {
    response = await client().requestGeneration({
      params: {
        projectId,
        creditSource: creditSourceToProto(creditSource),
        workflow: {
          case: 'visionBoardTextToImage',
          value: {
            prompt: prompt || '',
            aspectRatio: aspectRatio || '',
            useThinking: Boolean(useThinking),
            additionalReferenceAssetIds: additionalReferenceAssetIds || [],
          },
        },
      },
    });
  } catch (error) {
    throw toLegacyRequestError(error);
  }

  const result = response.result;
  if (!result?.generationId) {
    throw new Error('RequestGeneration (board) returned no generation id');
  }

  return { generationId: result.generationId, status: statusToLegacy(result.status) };
}

export async function listVisionBoardGenerations({ projectId, pageSize = PAGE_SIZE, pageToken = null, shouldContinue } = {}) {
  const generations = [];
  let cursor = pageToken;

  do {
    if (shouldContinue && !shouldContinue()) throw new DOMException('Image renewal paused', 'AbortError');
    const pagination = { pageSize };
    if (cursor) {
      pagination.pageToken = { case: 'nextToken', value: cursor };
    }

    const response = await client().listVisionBoardGenerations({
      params: { projectId },
      pagination,
    });

    const { generations: page, nextPageToken } = listBoardResponseToGenerations(response);
    generations.push(...page);
    cursor = nextPageToken;
  } while (cursor);

  return generations;
}

export async function deleteVisionBoardGenerations({ projectId, generationIds }) {
  await client().deleteVisionBoardGenerations({
    params: { projectId, generationIds },
  });
}

/** Optimize a Vision Board prompt via gRPC. Resolves to `{ optimizedPrompt }`. */
export async function optimizeVisionBoardPrompt({ prompt }) {
  const response = await client().optimizeVisionBoardPrompt({
    params: { prompt },
  });
  const optimizedPrompt = response.result?.optimizedPrompt;
  if (!optimizedPrompt) {
    throw new Error('OptimizeVisionBoardPrompt returned no optimized prompt');
  }
  return { optimizedPrompt };
}

/** Optimize an image-generation edit prompt via gRPC. Pass exactly one of `generationId` /
 * `imageId` to select the image to ground the optimize against. Resolves to `{ optimizedPrompt }`. */
export async function optimizeGenerationPrompt({ prompt, generationId, imageId }) {
  const source = generationId
    ? { case: 'generationId', value: generationId }
    : { case: 'imageId', value: imageId };

  const response = await client().optimizeGenerationPrompt({
    params: { prompt, source },
  });
  const optimizedPrompt = response.result?.optimizedPrompt;
  if (!optimizedPrompt) {
    throw new Error('OptimizeGenerationPrompt returned no optimized prompt');
  }
  return { optimizedPrompt };
}

/** Download vision board generations via the media service. Returns the raw fetch Response for
 * blob/streaming handling by the caller. */
export async function downloadVisionBoard({ projectId, generationIds }) {
  const token = await getIdTokenForRequest();
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return fetch(
    `${MEDIA_BASE_URL}/vision/projects/${projectId}/vision-board/download`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({ generationIds }),
    },
  );
}

export async function addToArtboard({ projectId, generationId, locationId }) {
  const token = await getIdTokenForRequest();
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const body = locationId != null ? { locationId } : {};
  const res = await fetch(
    `${MEDIA_BASE_URL}/vision/projects/${projectId}/generations/${generationId}/add-to-artboard`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    throw new Error('add-to-artboard failed');
  }
}
